import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { clearPath, resolve, resolveValue, writePath } from "./cascade";
import { translateSetting, translateSettings } from "./translate";
import type { Translation } from "./translate";
import { CUSTOM_CSS_KEY, defaultOnOf, translatableSettings } from "./catalog";
import {
  ORDER_KEY,
  FIRST_INSTANCE,
  instanceKey,
  orderedEntries,
} from "../funnel/order";
import { CLOSED_AT_KEY, OPEN_AT_KEY } from "../funnel/cloture";
import { PAGES_ORDER_KEY } from "../pages/order";
import { addedInstances, instancesKey } from "../pages/repeatable";
import { REPEATABLE_PAGES } from "../pages/registry";
import {
  REFERENCE_KEY,
  SHOW_KEY,
  fetchLanguages,
  languagesOf,
  referenceOf,
  rowsFrom,
  fetchTypos,
} from "./remote";
import {
  translatablesOf,
  writeTranslations,
  translated,
  type Translatable,
  type Translations,
} from "../funnel/formI18n";
import { useForms, useTranslations } from "../funnel/formQueries";
import { saveLanguage } from "../funnel/participant";
import { useParticipation } from "../funnel/useParticipation";
import {
  languageToAdopt,
  preferredLanguage,
  readLanguage,
  writeLanguage,
} from "./langue";
import { ApiError } from "../api/client";
import { useEvent } from "../context/EventProvider";
import { readMode, useAutoSave, writeMode, type SaveMode } from "./useAutoSave";
import type { SyncState } from "./sync";
import { DEFAULT_LANGUAGE, LANGUAGES, SEED, TYPOS } from "./seed";
import * as store from "./store";
import type { Rows } from "./store";
import type { Resolution, Scope } from "./types";
import { fill, type Say } from "./say";

/**
 * ═══ LES CLÉS QUI NE SE CASCADENT PAS ═══
 *
 * Leur valeur est une propriété de l'ÉVÉNEMENT — la structure du parcours et des pages, les
 * langues offertes, la feuille de style — pas de la langue ni de la typologie. Écrites à la
 * portée courante (depuis l'anglais, ou une typologie sélectionnée), elles partaient dans une
 * ligne que la lecture ne consulte pas — donc clic mort à l'écran — ET faisaient CASCADER la
 * structure : un parcours différent par profil, ce que `funnel/order.ts` interdit. `write`
 * les route donc toujours par `writeBase`.
 */
/**
 * ⚠️ EXPORTÉE POUR ÊTRE VÉRIFIÉE, et non pour être lue ailleurs : `write` est le seul
 * appelant. Une liste tenue à la main dont l'oubli ne se voit qu'à l'usage — des
 * semaines plus tard, sur la version anglaise du site — est exactement ce qu'un test
 * doit pouvoir tenir contre le catalogue. Voir `settings/context.test.ts`.
 */
export const COMMON_KEYS = new Set<string>([
  ORDER_KEY,
  PAGES_ORDER_KEY,
  /**
   * ⚠️ IL N'Y A QU'UNE HEURE DE CLÔTURE, comme il n'y a qu'une caméra dans la salle.
   * Écrite à la portée courante, une échéance posée depuis la version anglaise du site
   * n'aurait fermé QUE pour les anglophones : la cascade ne consulte, en dernier
   * recours, que la ligne de la langue de référence. Et le défaut serait resté
   * invisible jusqu'au jour dit — c'est tout l'objet de cette liste.
   *
   * ⚠️ L'INTERRUPTEUR VOISIN, LUI, N'Y EST PAS, et cette asymétrie est voulue : il agit
   * SOUS LES YEUX de l'organisateur, qui voit l'écran se verrouiller et corrigerait
   * aussitôt. L'échéance, elle, se règle des semaines à l'avance et ne se vérifie
   * jamais. Le faire basculer ici changerait par ailleurs le comportement d'événements
   * déjà configurés, ce qu'une nouvelle clé ne risque pas.
   */
  CLOSED_AT_KEY,
  /**
   * ⚠️ ET L'HEURE D'OUVERTURE AVEC ELLE, mot pour mot le même raisonnement : elle se
   * pose des semaines à l'avance et ne se vérifie jamais. Posée depuis la version
   * anglaise du site, elle n'aurait fait attendre QUE les anglophones — et le défaut
   * serait resté invisible jusqu'au jour dit, c'est-à-dire trop tard.
   */
  OPEN_AT_KEY,
  ...REPEATABLE_PAGES.map(instancesKey),
  CUSTOM_CSS_KEY,
  /**
   * ⚠️ TOUTE CLÉ STRUCTURELLE EST AUSSI UNE CLÉ COMMUNE, et les deux listes ne
   * doivent donc pas pouvoir diverger. Elles disent la même chose vue des deux
   * bouts : `STRUCTURAL_KEYS` efface la clé des autres lignes en écrivant, celle-ci
   * l'écrit toujours sur la ligne de référence. Tenues à la main, il a suffi
   * d'ajouter une clé à l'une pour que l'écriture reparte dans une ligne que la
   * lecture ne consulte pas.
   */
  ...store.STRUCTURAL_KEYS,
]);

/**
 * THE SETTINGS PROVIDER.
 *
 * It holds three things and no more:
 *   — the reading SCOPE: "as" which language and which typology,
 *   — resolution of a key through the cascade,
 *   — writing, at the current scope.
 *
 * Writes are applied to the in-memory rows before being persisted, which is what
 * makes the panel feel alive: the organiser sees their text change while typing,
 * not after a round trip.
 */

type SettingsApi = {
  scope: Scope;
  /**
   * The rows themselves, for the one reader that needs to look ACROSS scopes:
   * the recap, which resolves every setting for every typology at once. The
   * panel never touches these — it asks for one value at a time.
   */
  rows: Rows;
  /** The resolved value — what the participant described by the scope would see. */
  value: (key: string) => unknown;
  /**
   * LA MÊME LECTURE, POUR UNE AUTRE TYPOLOGIE — sans rien déplacer.
   *
   * ⚠️ ELLE EXISTE POUR UN SEUL LECTEUR : celui qui doit comparer ce que le participant
   * recevait AVANT de changer de typologie à ce qu'il recevra APRÈS (voir
   * `api-v1/api/helpers/cartOnTypoChange.js`). `value` ne sait lire que la portée courante, et la
   * portée courante suit déjà la réponse qu'on vient de donner : au moment d'écrire, la
   * typologie d'avant n'est plus lisible autrement.
   *
   * Langue et référence sont celles de la portée courante ; seule la typologie change.
   */
  valueAs: (typoId: number | null) => (key: string) => unknown;
  /** The value AND its provenance — what the panel needs. */
  detail: (key: string) => Resolution;
  /** Writes at the current scope. */
  write: (key: string, value: unknown) => void;
  /**
   * Écrit au NIVEAU COMMUN — langue de référence, aucune typologie.
   *
   * ⚠️ Un seul appelant, et il justifie l'existence de cette seconde porte : la
   * complétion de la reprise. Ce qu'elle pose vaut pour tout l'événement, et
   * `write` l'aurait rangé dans la langue affichée à ce moment-là : un organisateur
   * qui lance la reprise depuis la version anglaise se serait retrouvé avec un titre
   * d'accueil en surcharge anglaise et rien dans la langue de référence.
   */
  writeBase: (key: string, value: unknown) => void;
  /** Removes the override set at the current scope; the inherited value returns. */
  clear: (key: string) => void;
  /**
   * How many other languages are still SILENT on this key, at the current scope.
   * The translate affordance is quiet at zero and insistent otherwise.
   */
  missing: (key: string) => number;
  /** Fills every other language from this one, with Claude. Reference language only. */
  translate: (key: string) => Promise<void>;
  /** True while a translation is in flight, by key. */
  translating: string | null;
  /**
   * Settings, ACROSS EVERY SCREEN, still missing at least one language — what
   * the "translate everything" button offers to fill.
   */
  toTranslate: () => number;
  /** Fills them all, one after another. Reference language only. */
  translateAll: () => Promise<{ done: number; failed: number }>;
  /** Automatic, or only when the organiser asks. Their preference, kept locally. */
  saveMode: SaveMode;
  setSaveMode: (mode: SaveMode) => void;
  /** Something is waiting to be sent — what the leave-warning watches. */
  unsaved: boolean;
  /** Sends what is waiting, now. The button in manual mode. */
  saveNow: () => Promise<void>;
  /** Where the automatic saving stands — the panel shows it. */
  saveState: SyncState;
  /**
   * The server REFUSES to write, for saving or for translating alike. One flag for
   * one cause: both routes want a role on the domain, and the panel has one thing
   * to say about it rather than a message per button.
   */
  refused: boolean;
  /** False in the seeded demonstration: nothing to save to. */
  saving: boolean;
  /**
   * The event's own configuration is loaded — see the note where it is computed.
   * The shell shows nothing of the site before this is true.
   */
  ready: boolean;
  /**
   * ═══ LA LANGUE LUE — CELLE OÙ LE PANNEAU ÉCRIT ═══
   *
   * ⚠️ ELLE NE MÉMORISE RIEN, et c'est exactement la distinction que `setTypo` fait déjà
   * avec `setViewerTypo` : regarder « en tant que » n'est pas CHOISIR. L'organisateur qui
   * bascule le site en anglais rédige la version anglaise ; il n'exprime aucune
   * préférence, et lui poser l'anglais sur son compte le lui rendrait à chaque visite.
   * Le sélecteur de la barre d'organisation n'appelle qu'elle ; celui du site, le `Shell`
   * lui fait appeler celle-ci ou `chooseLanguage` selon le mode.
   */
  setLanguage: (languageId: number) => void;
  /**
   * ═══ LE GESTE DU VISITEUR — celui qui engage ═══
   *
   * Le sélecteur du SITE, et lui seul. Change la langue lue ET la retient : dans ce
   * navigateur, et sur le COMPTE, pour qu'on la retrouve d'un appareil à l'autre.
   */
  chooseLanguage: (languageId: number) => void;
  /** La typologie « en tant que » de la barre — celle où le panneau ÉCRIT. */
  setTypo: (typoId: number | null) => void;
  /**
   * LA TYPOLOGIE DE CELUI QUI REGARDE — celle qui décide de ce que le site SERT.
   *
   * ⚠️ SÉPARÉE DE `setTypo`, ET C'EST TOUT L'INTÉRÊT : un participant qui choisit son
   * profil doit recevoir la configuration de ce profil, sans pour autant déplacer le
   * sélecteur de l'organisateur. Voir `funnel/viewerTypo.ts`, qui porte la règle et
   * raconte les deux façons de s'y tromper.
   *
   * Posée par le SHELL, qui est le seul à connaître à la fois le mode et les réponses.
   */
  setViewerTypo: (typoId: number | null) => void;
  /**
   * The languages the EVENT has, not a fixed list: an event with three is not
   * an event with six, and offering a language it does not carry lets an
   * organiser write into a row nobody will ever read.
   */
  languages: ReadonlyArray<{ id: number; name: string; short: string }>;
  /** The event's own participant typologies. */
  typos: ReadonlyArray<{ id: number | null; name: string }>;
  /**
   * Les formulaires que cet écran peut servir : ceux de l'événement ET ceux du DOMAINE,
   * comme la route les rend. `eventId` à `null` désigne les seconds.
   */
  forms: ReadonlyArray<{
    id: number;
    name: string;
    type: "U" | "C" | null;
    eventId: number | null;
  }>;
  /**
   * LES JOURS DE L'ÉVÉNEMENT, publiés ici comme ses typologies et ses formulaires.
   *
   * ⚠️ ILS DÉCIDENT DE L'EXISTENCE D'UNE ÉTAPE, et c'est ce qui les met là plutôt que
   * de laisser chaque écran appeler `useEvent()` : le fil se calcule à partir d'eux
   * (un événement d'un seul jour n'a pas de dates à demander), et le fil est lu par le
   * fil d'Ariane, la garde du shell et chaque bouton « Continuer ». Une seule source,
   * ou ces trois-là finissent par ne pas compter les mêmes étapes.
   *
   * Aucune requête de plus : ils voyagent avec le contexte de l'événement.
   */
  days: ReadonlyArray<{ id: number; day: string }>;
  /**
   * ═══ LE DICTIONNAIRE DU PRODUIT, PAR LANGUE ═══
   *
   * Les mots que le site emploie de lui-même — « Retour », « Enregistrer », « Étape » —
   * et les défauts de ses réglages. Ils viennent de `GET /languages`, qui fusionne trois
   * niveaux : le général (`Languages.translations`, partagé par tout le produit), celui
   * du domaine et celui de l'événement (`LanguageEvents`). C'est le magasin que la V1
   * interroge avec son `t()`, et le back office celui qu'il écrit.
   *
   * ⚠️ IL EST DÉJÀ CHARGÉ, ET C'EST CE QUI REND `useT` GRATUIT : le fournisseur le lit
   * pour les libellés de formulaire depuis le début (`useTraductions`). Une seconde
   * requête aurait doublé un aller-retour du démarrage pour la même réponse.
   */
  translations: Translations;
  /**
   * EVERY language the product knows — what the panel offers to turn on, as
   * opposed to `languages`, which is what the event currently has.
   */
  catalogue: ReadonlyArray<{ id: number; key: string; name: string }>;
};

const SettingsContext = createContext<SettingsApi | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const event = useEvent();

  /**
   * ═══ THE EVENT'S OWN SETTINGS ═══
   *
   * The catalogue of languages is public and never changes during a visit, so it
   * is fetched once and kept. The rows themselves ride along with the context —
   * one public call for the event AND its whole configuration, see `remote.ts`.
   *
   * ⚠️ THE SEED IS ONLY A STARTING SHAPE, never what is shown once an event is
   * known. Until this landed, the panel WROTE to the real database and DISPLAYED
   * seeded text: a translation saved correctly and then appeared to do nothing,
   * because what was on screen had never come from the event.
   */
  const { data: catalogue, isFetched: catalogueSettled } = useQuery({
    queryKey: ["languages"],
    queryFn: ({ signal }) => fetchLanguages(signal),
    staleTime: Infinity,
  });

  /**
   * The typologies come from their OWN public route, not from the settings:
   * `extended.typos` is a leftover that no longer matches what the event has.
   */
  const { data: eventTypos, isFetched: typosSettled } = useQuery({
    queryKey: ["typos", event?.domainId, event?.eventId],
    queryFn: ({ signal }) =>
      fetchTypos(event!.domainId, event!.eventId, signal),
    enabled: !!event,
    staleTime: Infinity,
  });

  /**
   * IS THE EVENT'S REAL CONFIGURATION IN HAND?
   *
   * Until it is, the rows are the seed and the theme paints DEMONSTRATION colours:
   * a visitor saw the default blue for a moment, then the event's palette. The
   * shell waits on this rather than showing a site that is not yet the right one.
   *
   * ⚠️ SETTLED, NOT SUCCEEDED. `isFetched` turns true on an error too, and that is
   * deliberate: a language catalogue that fails to load would otherwise hold the
   * page on a loading screen for good. Falling back to the seed is today's
   * behaviour and merely imperfect; hanging is worse.
   *
   * The typologies are waited on for the same reason. Read too early, `typos` is
   * an empty list, which is indistinguishable from "this event has none" — and the
   * funnel then drops its typology step and puts it back a moment later, so the
   * breadcrumb counted two steps before counting three.
   */
  /**
   * The event's FORMS. Needed by the panel — the informations step names one rather
   * than building it — so they are fetched once and kept for the visit.
   *
   * Les traductions du produit suivent. Les DEUX lectures sont celles que partagent tous
   * les écrans qui servent un formulaire — voir `funnel/formQueries.ts`, où elles sont
   * déclarées une seule fois.
   */
  const eventForms = useForms();
  const translations = useTranslations();

  const client = useQueryClient();

  const ready = !!event && catalogueSettled && typosSettled;

  const remote = useMemo(() => {
    if (!event || !catalogue) return null;
    return {
      rows: rowsFrom(event.settingsAll, catalogue),
      // The keys the event has a row for — the fallback for an event that
      // predates the "which languages" setting.
      fallbackKeys: Object.keys(event.settingsAll),
      // Every language the CATALOGUE has, so the panel can offer to turn one on.
      catalogue,
    };
  }, [event, catalogue]);

  /* Qui est connecté : sa langue passe devant tout le reste. Aucune requête de plus,
     c'est celle que le tunnel fait déjà. */
  const { me } = useParticipation();

  // The event's rows REPLACE what was there, they are not merged onto the seed:
  // a setting the event deliberately leaves empty would otherwise show
  // demonstration text instead of nothing.
  const [rows, setRows] = useState(() =>
    remote ? remote.rows : store.load(SEED),
  );
  /**
   * ⚠️ PENDANT LE RENDU, PAS DANS UN EFFET (bug #350). Posées par un effet, les lignes
   * de l'événement n'arrivaient qu'au rendu SUIVANT : le premier où `ready` était vrai
   * servait encore le SEED, et l'onglet, écrit par un effet du Shell qui passe avant
   * celui-ci, prenait « Salon Tech 2026 » le temps d'un aller-retour.
   */
  const [rowsOf, setRowsOf] = useState(remote);
  if (remote !== rowsOf) {
    setRowsOf(remote);
    if (remote) setRows(remote.rows);
  }
  /**
   * ═══ LA LANGUE LUE, ET D'OÙ ELLE VIENT ═══
   *
   * ⚠️ ELLE NE VENAIT DE NULLE PART : l'état partait sur la langue de référence à chaque
   * chargement, donc quelqu'un qui avait choisi l'anglais retrouvait le français à sa
   * visite suivante, et son compte n'en gardait aucune trace. Trois sources désormais,
   * dans l'ordre de la V1 : le compte, ce navigateur, le navigateur. Voir
   * `settings/langue.ts`.
   *
   * ⚠️ LE STOCKAGE LOCAL EST LU DÈS L'INITIALISATION, pas dans un effet : lu après coup,
   * la page s'affichait une fraction de seconde dans la langue de référence avant de
   * basculer. Le compte, lui, arrive forcément plus tard — il faut une requête.
   */
  const [languageId, setLanguageId] = useState<number>(
    () => readLanguage(globalThis.localStorage) ?? DEFAULT_LANGUAGE,
  );
  /** Le visiteur a tranché lui-même : plus rien ne revient dessus. */
  const languageChosen = useRef(false);
  const [typoId, setTypoId] = useState<number | null>(null);
  /** Celle du VISITEUR — voir `setViewerTypo` sur le type, et `funnel/viewerTypo.ts`. */
  const [viewerTypo, setViewerTypo] = useState<number | null>(null);

  /**
   * Derived from the LIVE rows, not from the API payload. Turning a language on
   * has to show immediately — computed from what was fetched, the list only
   * moved after a reload, so the click appeared to do nothing.
   */
  const active = useMemo(
    () =>
      remote ? languagesOf(rows, remote.catalogue, remote.fallbackKeys) : null,
    [remote, rows],
  );
  const languages = active ?? LANGUAGES;
  const defaultLanguageId = useMemo(
    () => (active ? referenceOf(rows, active) : DEFAULT_LANGUAGE),
    [active, rows],
  );
  const typos = eventTypos ?? (remote ? [] : TYPOS);

  const scope = useMemo<Scope>(
    () => ({
      // Falls back to the reference until a language is chosen, AND when the
      // chosen one does not exist here: an id kept from another event would
      // resolve to nothing at all.
      languageId: languages.some((l) => l.id === languageId)
        ? languageId
        : defaultLanguageId,
      defaultLanguageId,
      /**
       * ⚠️ CELLE DU VISITEUR PASSE DEVANT, ET ELLE N'EST POSÉE QU'EN MODE VISITEUR — le
       * shell s'en assure (voir `funnel/viewerTypo.ts`). En mode organisateur elle vaut
       * `null`, donc c'est le sélecteur « en tant que » qui décide, comme avant : le
       * panneau continue d'écrire là où il annonce écrire.
       *
       * Une seule portée pour les deux usages, et c'est voulu : ce que l'organisateur
       * PRÉVISUALISE doit être exactement ce qu'un participant de cette typologie
       * REÇOIT. Deux résolutions séparées auraient fini par diverger, et la divergence
       * se serait vue le jour de l'événement, pas dans le panneau.
       */
      typoId: viewerTypo ?? typoId,
    }),
    [languageId, defaultLanguageId, languages, typoId, viewerTypo],
  );

  /**
   * ⚠️ LE COMPTE PASSE DEVANT, DÈS QU'IL ARRIVE, mais jamais par-dessus un choix fait à
   * l'instant : sans ce verrou, basculer en anglais puis voir la réponse de `me` revenir
   * aurait remis la page en français sous la main du visiteur.
   */
  useEffect(() => {
    if (languageChosen.current || languages.length === 0) return;
    const preferred = preferredLanguage({
      account: me?.languageId,
      stored: readLanguage(globalThis.localStorage),
      browser: globalThis.navigator?.language,
      languages: languages,
    });
    if (preferred) setLanguageId(preferred);
  }, [me?.languageId, languages]);

  /**
   * LIRE DANS UNE AUTRE LANGUE, SANS RIEN ENGAGER.
   *
   * ⚠️ LE VERROU EST POSÉ ICI AUSSI, et il le faut : ce qu'on vient de choisir
   * délibérément — fût-ce pour prévisualiser — ne doit pas se faire écraser par la réponse
   * du compte qui arrive ensuite.
   */
  const readInLanguage = useCallback((id: number) => {
    languageChosen.current = true;
    setLanguageId(id);
  }, []);

  /**
   * CHANGER DE LANGUE, ET S'EN SOUVENIR.
   *
   * ⚠️ TROIS ÉCRITURES POUR UN SEUL GESTE, et chacune sert à un moment différent : l'état
   * pour l'écran, le stockage pour le prochain chargement de ce navigateur, le COMPTE pour
   * partout ailleurs. Le compte est le seul qui traverse les appareils.
   *
   * ⚠️ ET SEULEMENT POUR UN VISITEUR. Le sélecteur du site sert aux deux modes (la barre
   * d'organisation a le sien, qui ne fait que lire) : c'est donc le `Shell` qui choisit
   * lequel des deux gestes celui du site déclenche. Un organisateur qui bascule en
   * anglais rédige la version anglaise, il n'exprime pas une préférence, et la lui écrire
   * lui rendrait l'anglais à chaque visite — sur son propre site de participant. Même distinction que `setTypo` et `setViewerTypo`.
   *
   * ⚠️ L'ÉCRITURE NE BLOQUE RIEN ET SON ÉCHEC EST TU : la langue a déjà changé à l'écran
   * quand la requête part. Voir `saveLanguage`.
   */
  const rememberLanguage = useCallback(
    (id: number) => {
      readInLanguage(id);
      writeLanguage(globalThis.localStorage, id);
      if (event && typeof me?.id === "number")
        void saveLanguage(event.domainId, id)
          .then(() => client.invalidateQueries({ queryKey: ["participant"] }))
          .catch(() => undefined);
    },
    [readInLanguage, event, me?.id, client],
  );

  /**
   * LE CHOIX FAIT AVANT LA SESSION RATTRAPE LE COMPTE (bug #331).
   *
   * `rememberLanguage` n'écrit sur le compte que s'il y a déjà une session : quelqu'un qui
   * passe le site en anglais PUIS se connecte gardait un compte sans langue, et ses emails
   * partaient dans la langue de référence. Tous les chemins y passent : création par
   * code (apix crée le compte à l'envoi du code), connexion par lien ou par mot de passe.
   *
   * ⚠️ UNE FOIS PAR COMPTE : la réponse de `me` revient après l'écriture, sans langue
   * encore si la relecture est en retard ; sans ce verrou, l'écriture repartirait.
   */
  const adoptedFor = useRef<number | null>(null);
  useEffect(() => {
    if (!event || typeof me?.id !== "number" || adoptedFor.current === me.id)
      return;
    const id = languageToAdopt(
      me.languageId,
      readLanguage(globalThis.localStorage),
    );
    if (!id) return;
    adoptedFor.current = me.id;
    void saveLanguage(event.domainId, id)
      .then(() => client.invalidateQueries({ queryKey: ["participant"] }))
      .catch(() => undefined);
  }, [event, me?.id, me?.languageId, client]);

  const layers = useMemo(() => store.layersFor(rows, scope), [rows, scope]);

  const value = useCallback(
    (key: string) => resolveValue(layers, scope, key),
    [layers, scope],
  );
  const valueAs = useCallback(
    (typoId: number | null) => (key: string) =>
      resolveValue(layers, { ...scope, typoId }, key),
    [layers, scope],
  );
  const detail = useCallback(
    (key: string) => resolve(layers, scope, key),
    [layers, scope],
  );

  const [saveMode, setMode] = useState<SaveMode>(readMode);
  const setSaveMode = useCallback((mode: SaveMode) => {
    writeMode(mode);
    setMode(mode);
  }, []);

  const {
    state: saveState,
    record,
    forget,
    learn,
    stored,
    flush,
    unsaved,
    enabled: saving,
  } = useAutoSave(event, saveMode, remote?.rows ?? null);

  /**
   * Cette liste de langues est-elle celle que le site déduirait sans réglage, maintenant
   * comme au prochain chargement, alors que le serveur n'en tient aucune ? Voir
   * `writeBase`.
   */
  const impliedBy = useCallback(
    (next: unknown) => {
      if (!remote || !Array.isArray(next)) return false;
      if (Object.values(stored()).some((row) => row?.[SHOW_KEY] !== undefined))
        return false;
      const keysOf = (fallback: ReadonlyArray<string>) =>
        languagesOf({}, remote.catalogue, fallback).map((l) => l.key);
      // Les lignes de la page comptent aussi : une langue écrite pendant la visite aura
      // une ligne au rechargement, et la déduction l'y allumera.
      const live = remote.catalogue
        .filter((l) => rows[l.id] !== undefined)
        .map((l) => l.key);
      const chosen = new Set(next.map(String));
      return [
        keysOf(remote.fallbackKeys),
        keysOf([...remote.fallbackKeys, ...live]),
      ].every(
        (keys) => keys.length === chosen.size && keys.every((k) => chosen.has(k)),
      );
    },
    [remote, rows, stored],
  );

  /**
   * L'envoi DEMANDÉ, par opposition au cycle. Un refus arrête l'automatique (voir
   * `useAutoSave`) ; ce bouton-là passe outre, parce que c'est le seul geste qui
   * puisse dire « les droits ont changé, réessaie ».
   */
  const saveNow = useCallback(() => flush(true), [flush]);

  /**
   * LE SERVEUR A REFUSÉ UNE ÉCRITURE — pour la traduction comme pour
   * l'enregistrement. Un seul drapeau parce que c'est une seule cause : les deux
   * routes exigent un rôle sur le domaine, et le panneau n'a qu'une chose à dire.
   */
  const [translateRefused, setTranslateRefused] = useState(false);

  /** Même geste, au niveau commun — voir le commentaire du type. */
  const writeBase = useCallback(
    (key: string, next: unknown) => {
      /**
       * ⚠️ LA LISTE DES LANGUES QUI REDEVIENT CELLE QU'ON DÉDUIRAIT SANS ELLE S'EFFACE.
       * Sans liste, le site active les langues qui ont une ligne (`languagesOf`) ; la
       * ligne des langues écrit pourtant toujours une liste. Cocher « English » puis le
       * décocher rangeait donc `["FR"]` là où il n'y avait rien : une modification en
       * attente, et un réglage compté au récapitulatif, pour un écran revenu à l'identique
       * (#295). Ce n'est fait que si le serveur ne tient aucune liste (sinon c'est elle,
       * retrouvée, qui annule le geste dans la file), et si la déduction est la même au
       * rechargement : une langue qui a gagné une ligne depuis s'y rallumerait.
       */
      if (key === SHOW_KEY && impliedBy(next)) {
        setRows((previous) =>
          store.clear(
            previous,
            { ...scope, languageId: scope.defaultLanguageId, typoId: null },
            key,
          ),
        );
        forget(scope.defaultLanguageId, [key]);
        return;
      }

      const entry = writePath(
        { ...scope, languageId: scope.defaultLanguageId, typoId: null },
        key,
        next,
      );

      /**
       * ⚠️ UNE VALEUR STRUCTURELLE PÉRIMÉE SUR UNE AUTRE LIGNE DOIT ÊTRE EFFACÉE,
       * ET PAS SEULEMENT EN MÉMOIRE. La lecture (`structural`, dans `remote.ts`)
       * prend la PREMIÈRE ligne qui porte la clé, l'écriture pose toujours sur la
       * ligne de référence : sur un événement dont la ligne française dit déjà
       * « EN » alors que la référence EST l'anglais, la valeur écrite est rangée
       * là où la lecture ne regarde jamais. Le sélecteur revenait à sa valeur
       * d'avant, sans un mot. Vécu sur l'événement 408 : impossible de changer la
       * langue de référence.
       *
       * `store.write` nettoie les autres lignes en mémoire ; il faut le dire aussi
       * au serveur, sinon la valeur périmée regagne au rechargement. Une fusion ne
       * sait pas supprimer, d'où `forget` et sa liste `unset`.
       */
      if (store.STRUCTURAL_KEYS.has(key)) {
        for (const [id, row] of Object.entries(rows)) {
          if (Number(id) !== entry.languageId && row && key in row) {
            forget(Number(id), [key]);
          }
        }
      }

      setRows((previous) => store.write(previous, entry));
      record(entry.languageId, entry.extended);
    },
    [scope, record, forget, rows, impliedBy],
  );

  const write = useCallback(
    (key: string, next: unknown) => {
      // ⚠️ LES CLÉS STRUCTURELLES VONT AU NIVEAU COMMUN, quelle que soit la portée affichée.
      // Voir `CLES_COMMUNES` : sans ça, régler les langues ou réordonner les étapes depuis
      // l'anglais ou une typologie était un clic mort qui semait une surcharge parasite.
      if (COMMON_KEYS.has(key)) {
        writeBase(key, next);
        return;
      }
      const entry = writePath(scope, key, next);
      // Applied in memory FIRST, so the screen changes under the organiser's
      // fingers; the server hears about it on the next cycle.
      setRows((previous) => store.write(previous, entry));
      record(entry.languageId, entry.extended);
    },
    [scope, record, writeBase],
  );

  const [translating, setTranslating] = useState<string | null>(null);

  /**
   * What `translating` holds while the WHOLE screen is being translated.
   *
   * Every per-row button is disabled on `translating !== null`, and that is the
   * point: one batch is one read-merge-write per language row, and a row button
   * pressed in the middle of it would merge against the version it read before.
   * No key matches this one, so no row shows a spinner — the bar does.
   */
  const ALL = "*";

  /**
   * Applies what came back from the route — it has ALREADY saved it, so this
   * only catches the panel up. Nothing is queued, or the next save would send
   * the server back what it just wrote to us.
   */
  const applyTranslations = useCallback(
    (entries: ReadonlyArray<{ key: string; translations: Translation[] }>) => {
      const written = entries.flatMap(({ key, translations }) =>
        translations.map((t) => ({
          languageId: t.languageId,
          extended: scope.typoId
            ? { typosV2: { [String(scope.typoId)]: { [key]: t.value } } }
            : { [key]: t.value },
        })),
      );
      // Déjà sur le serveur : la file ne doit pas croire qu'y revenir ne change rien.
      for (const entry of written) learn(entry.languageId, entry.extended);
      setRows((previous) =>
        written.reduce((acc, entry) => store.write(acc, entry), previous),
      );
    },
    [scope.typoId, learn],
  );

  /**
   * How many languages are still silent on this key.
   *
   * ⚠️ COUNTED AGAINST THE EVENT'S LANGUAGES, not the seeded list, and against
   * the EVENT'S reference, not the constant. Counted against the seed it looked
   * for Spanish under id 3 — the seed's id — while this event's Spanish is id 4.
   * A row that could never exist was always missing, so the button read
   * "Traduire (1)" for ever, including right after a translation that had
   * worked.
   */
  const missing = useCallback(
    (key: string) => {
      /**
       * ⚠️ WITH A TYPOLOGY SELECTED, NOTHING IS MISSING UNTIL SOMETHING IS
       * WRITTEN HERE.
       *
       * A setting with no override for this typology has nothing
       * typology-specific to spread. Counting the other languages as "missing"
       * offered to translate it — and accepting would have written the INHERITED
       * value as a typology override in every language, freezing a value that
       * was only ever passing through. Overrides nobody asked for, in languages
       * nobody checked.
       */
      if (
        scope.typoId &&
        store.ownValue(rows, scope.defaultLanguageId, scope.typoId, key) ===
          undefined
      ) {
        return 0;
      }

      return languages.filter(
        (l) =>
          l.id !== scope.defaultLanguageId &&
          store.ownValue(rows, l.id, scope.typoId, key) === undefined,
      ).length;
    },
    [rows, languages, scope.defaultLanguageId, scope.typoId],
  );

  const translate = useCallback(
    async (key: string) => {
      const source = resolveValue(layers, scope, key);
      if (!event || typeof source !== "string" || !source.trim()) return;

      setTranslating(key);
      try {
        // Every language that is ON, except the one being translated FROM.
        const targets = languages
          .filter((l) => l.id !== scope.defaultLanguageId)
          .map((l) => l.id);
        const translations = await translateSetting(
          event,
          scope,
          key,
          source,
          targets,
        );
        applyTranslations([{ key, translations }]);
      } catch (cause) {
        // ⚠️ SANS CE `catch`, la promesse partait en rejet non traité : le bouton
        // reprenait son libellé et RIEN n'était dit. Mesuré après la mise en place
        // de la garde de droits — 401 renvoyé, écran muet.
        if (cause instanceof ApiError && cause.isUnauthorized) {
          setTranslateRefused(true);
        } else {
          console.error("[traduction]", cause);
        }
      } finally {
        setTranslating(null);
      }
    },
    [event, layers, scope, languages, applyTranslations],
  );

  /**
   * The settings actually waiting to be translated, WITH what to translate them
   * from — ONE list, used both to count them and to send them.
   *
   * Two separate rules is how the button came to announce "10 traduits" for
   * three settings: the count skipped the ones with nothing to translate FROM,
   * the loop did not, and each of those returned instantly and was tallied as
   * done. A number that flatters is worse than no number.
   */
  const pending = useCallback(
    () =>
      /* ⚠️ LE PARCOURS RANGÉ, PAS LE CATALOGUE SEUL : les textes d'une étape AJOUTÉE
         (son titre, son sous-titre, son introduction, son nom) n'existent que si on dit
         combien d'instances sont servies. Sans lui, « Tout traduire » les sautait sans
         rien annoncer. */
      translatableSettings(
        orderedEntries(resolveValue(layers, scope, ORDER_KEY)),
        // Les typologies, pour que « Tout traduire » couvre AUSSI la précision d'une
        // typologie (`TYPO_NOTE_<id>`), engendrée par typologie et sinon jamais listée.
        typos.flatMap((t) => (t.id === null ? [] : [{ id: t.id, name: t.name }])),
        // Et les exemplaires ajoutés, sections comme pages formulaire : leur
        // sous-titre et leur contenu sont suffixés, donc invisibles de l'événement vide.
        addedInstances((key) => resolveValue(layers, scope, key)),
      ).flatMap((setting) => {
        const source = resolveValue(layers, scope, setting.key);
        return typeof source === "string" &&
          source.trim().length > 0 &&
          missing(setting.key) > 0
          ? [{ key: setting.key, source }]
          : [];
      }),
    [layers, scope, missing, typos],
  );

  /**
   * LES CHAÎNES DU FORMULAIRE QUI ATTENDENT UNE TRADUCTION.
   *
   * ⚠️ ELLES NE SONT PAS DES RÉGLAGES, et ne se rangent pas au même endroit : leur
   * magasin est celui du produit (`LanguageEvents.translations`), partagé avec la V1 et
   * le back office. D'où une seconde source ici, et une seconde écriture plus bas —
   * `applyTranslations` ne peut pas servir.
   *
   * ⚠️ ET PAS DE TYPOLOGIE. Un libellé de formulaire n'a qu'une valeur par langue : le
   * produit ne le décline pas par profil. Avec une typologie sélectionnée, ce sont donc
   * les mêmes chaînes qui restent à traduire, ni plus ni moins.
   */
  const pendingForm = useCallback((): Translatable[] => {
    /**
     * ⚠️ TOUS LES FORMULAIRES SERVIS, PAS SEULEMENT CELUI DES INFORMATIONS. L'étape
     * société en sert un second, et n'en lire qu'un laissait le formulaire de la fiche
     * en français sur un parcours par ailleurs entièrement traduit — exactement le
     * défaut que cette liste existe pour corriger, reproduit à l'étape suivante.
     */
    /* ⚠️ TOUS LES FORMULAIRES SERVIS : celui des informations (première instance ET
       suivantes ajoutées, dont les clés sont suffixées), celui de la société, et celui de
       l'accompagnant. En oublier un laissait son formulaire en français sur un parcours par
       ailleurs entièrement traduit — le défaut que cette liste existe pour corriger. */
    const instances = [
      ...new Set(
        orderedEntries(resolveValue(layers, scope, ORDER_KEY))
          .map((e) => e.instance)
          .filter((i): i is string => !!i && i !== FIRST_INSTANCE),
      ),
    ];
    const served = [
      "FO_MEETING_V2_INFO_FORM_ID",
      ...instances.map((i) => instanceKey("FO_MEETING_V2_INFO_FORM_ID", i)),
      "FO_MEETING_V2_COMPANY_FORM_ID",
      "FO_MEETING_V2_COMPANION_FORM",
    ]
      .map((key) => resolveValue(layers, scope, key))
      .flatMap((id) => {
        const form = (eventForms ?? []).find(
          (f) => String(f.id) === String(id),
        );
        return form ? [form] : [];
      });
    if (served.length === 0) return [];

    const targets = languages
      .filter((l) => l.id !== scope.defaultLanguageId)
      .map((l) => l.id);

    /* Dédoublonné par clé : deux étapes pointées sur le même formulaire feraient
       traduire chaque libellé deux fois, et le décompte annoncerait le double. */
    return [
      ...new Map(
        served.flatMap((form) =>
          translatablesOf(form).map((t) => [t.key, t] as const),
        ),
      ).values(),
    ].filter(({ key }) =>
      targets.some((id) => !(translations?.[id]?.[key] ?? "").trim()),
    );
  }, [layers, scope, eventForms, languages, translations]);

  /**
   * ⚠️ LE DÉCOMPTE ET LA BOUCLE LISENT LES MÊMES DEUX LISTES. Le commentaire au-dessus
   * de `pending` raconte ce piège : deux règles séparées, et le bouton annonçait « 10
   * traduits » pour trois réglages. Ajouter le formulaire d'un seul côté le rejouerait,
   * à l'identique.
   */
  const toTranslate = useCallback(
    () => pending().length + pendingForm().length,
    [pending, pendingForm],
  );

  /**
   * ONE REQUEST for the whole screen, and one write per language behind it.
   *
   * It used to walk the settings one by one — strictly sequential, because the
   * route reads a language row then writes it back and two calls in flight on
   * the same row lose one of the two. Correct, but linear: at a hundred settings
   * that is a hundred round trips the organiser waits through. The batching
   * moved into the route, which now merges everything it translated into a
   * single write per language, so the ordering problem no longer exists.
   *
   * A partial failure does not lose the rest: the route answers per setting, and
   * the ones it could not translate come back empty. They stay counted as
   * missing, which is exactly what the button will offer to retry.
   */
  const translateAll = useCallback(async () => {
    const waiting = pending();
    const waitingForm = pendingForm();
    const total = waiting.length + waitingForm.length;
    if (!event || total === 0) return { done: 0, failed: 0 };

    setTranslating(ALL);
    try {
      const targets = languages
        .filter((l) => l.id !== scope.defaultLanguageId)
        .map((l) => l.id);

      let done = 0;

      if (waiting.length > 0) {
        const answers = await translateSettings(event, scope, waiting, targets);
        applyTranslations(
          waiting.map(({ key }, i) => ({
            key,
            translations: answers[i] ?? [],
          })),
        );
        done += answers.filter((list) => list.length > 0).length;
      }

      if (waitingForm.length > 0) {
        /**
         * ⚠️ UN SECOND APPEL, ET NON LES DEUX LISTES DANS LE MÊME. `translateSettings`
         * passe chaque clé par `translationName(scope, …)`, qui l'enrobe du chemin de
         * la typologie quand il y en a une sélectionnée. Un libellé de formulaire n'a
         * qu'une valeur par langue — le produit ne le décline pas par profil — donc
         * l'enrober le nommerait faux au modèle. On l'appelle avec une portée sans
         * typologie.
         */
        const replies = await translateSettings(
          event,
          { ...scope, typoId: null },
          waitingForm.map(({ key, source }) => ({ key: key, source })),
          targets,
        );

        /* Une seule requête d'écriture, toutes langues et toutes clés : la route fait
           un `JSON_MERGE_PATCH` par langue, et des appels concurrents sur la même
           ligne se perdraient mutuellement. */
        const byLanguage = new Map<number, Map<string, string>>();
        waitingForm.forEach(({ key }, i) => {
          for (const t of replies[i] ?? []) {
            if (!byLanguage.has(t.languageId))
              byLanguage.set(t.languageId, new Map());
            byLanguage.get(t.languageId)!.set(key, t.value);
          }
        });

        await writeTranslations(event, byLanguage);
        /* Écrit dans l'autre magasin : sans invalidation, l'écran garderait les
           libellés d'avant jusqu'au rechargement. */
        await client.invalidateQueries({
          queryKey: ["form-i18n", event.domainId, event.eventId],
        });
        done += replies.filter((list) => list.length > 0).length;
      }

      return { done, failed: total - done };
    } catch (cause) {
      if (cause instanceof ApiError && cause.isUnauthorized) {
        setTranslateRefused(true);
      } else {
        console.error("[traduction]", cause);
      }
      return { done: 0, failed: total };
    } finally {
      setTranslating(null);
    }
  }, [
    event,
    scope,
    languages,
    pending,
    pendingForm,
    applyTranslations,
    client,
  ]);

  const clear = useCallback(
    (key: string) => {
      setRows((previous) => store.clear(previous, scope, key));
      // A merge cannot remove a key, so the removal is NAMED — see `clearPath`
      // and the `unset` list the route now takes.
      const { languageId, unset } = clearPath(scope, key);
      forget(languageId, unset);
    },
    [scope, forget],
  );

  const api = useMemo<SettingsApi>(
    () => ({
      scope,
      rows,
      value,
      valueAs,
      detail,
      write,
      writeBase,
      clear,
      setLanguage: readInLanguage,
      chooseLanguage: rememberLanguage,
      setTypo: setTypoId,
      setViewerTypo,
      readInLanguage,
      missing,
      translate,
      translating,
      toTranslate,
      translateAll,
      saveState,
      // Refusé une fois, on le dit tant que la page vit : les droits ne changent
      // pas d'eux-mêmes, et un message qui s'efface laisse quelqu'un retenter en
      // boucle sans jamais lire la cause.
      refused: saveState === "refused" || translateRefused,
      saveMode,
      setSaveMode,
      unsaved,
      saveNow,
      saving,
      ready,
      languages,
      typos,
      forms: eventForms ?? [],
      days: event?.days ?? [],
      translations: translations ?? {},
      catalogue:
        remote?.catalogue ?? languages.map((l) => ({ ...l, key: l.short })),
    }),
    [
      scope,
      rows,
      active,
      languages,
      typos,
      eventForms,
      event,
      remote,
      translations,
      value,
      valueAs,
      detail,
      write,
      writeBase,
      clear,
      missing,
      translate,
      translating,
      toTranslate,
      translateAll,
      saveState,
      saveMode,
      setSaveMode,
      unsaved,
      saveNow,
      saving,
      ready,
      translateRefused,
    ],
  );

  return (
    <SettingsContext.Provider value={api}>{children}</SettingsContext.Provider>
  );
}

export function useSettings(): SettingsApi {
  const api = useContext(SettingsContext);
  if (!api)
    throw new Error("useSettings must be used inside <SettingsProvider>");
  return api;
}

/** Shorthand read, for screens that do not need provenance. */
export function useSetting(key: string): unknown {
  return useSettings().value(key);
}

/**
 * ═══ UNE VALEUR QUI N'APPARTIENT À AUCUNE LANGUE ═══
 *
 * Cherchée dans TOUTES les lignes, la première qui la porte gagne — au lieu de la
 * langue lue puis la langue de référence, qui est la cascade normale.
 *
 * ⚠️ RÉSERVÉ À CE QUI N'EST PAS DE LA PROSE. Un titre se traduit, une adresse de
 * flux non : `youtube.com/embed/wTRFuwhcOuc` est la même vidéo en six langues.
 * L'appliquer à un texte ferait fuiter une phrase française dans la version
 * anglaise, ce que la cascade évite précisément.
 *
 * ⚠️ VÉCU, ET C'EST POURQUOI ÇA EXISTE. La V1 range sa vidéo dans `youtubeId`, écrit
 * sur la ligne de la langue où l'organisateur se trouvait — ici FR. Cet événement
 * déclare l'anglais comme langue de référence (`FO_MEETING_V2_LANGUAGE_DEFAULT`
 * vaut « EN »), donc la cascade lit EN et EN : la ligne FR n'est jamais consultée,
 * et l'écran affichait un cadre noir sans un mot pour dire pourquoi. Rien n'était
 * cassé — la cascade faisait exactement son travail sur une valeur qui n'aurait
 * jamais dû lui être confiée.
 *
 * Même mécanisme que `referenceOf` pour la langue de référence : voir `structural`
 * dans `remote.ts`, qui balaie les lignes pour la même raison.
 */
export function useStructuralSetting(key: string): unknown {
  const { rows } = useSettings();
  return useMemo(() => {
    for (const row of Object.values(rows ?? {})) {
      const value = (row as Record<string, unknown> | null)?.[key];
      if (value !== undefined) return value;
    }
    return undefined;
  }, [rows, key]);
}

/**
 * LA LANGUE LUE, SOUS L'ÉTIQUETTE QU'ATTEND `Intl` — « FR », « EN »…
 *
 * ⚠️ SEPT ÉCRANS LA RECALCULAIENT, mot pour mot, et ils formatent tous de l'argent ou
 * des dates : un seul qui aurait dérivé aurait écrit « 900.00 € » à un francophone ou
 * daté un jour de la veille. C'est `short` — la clé que la base porte — et non `name`,
 * qui est le nom lisible affiché par le panneau : les deux se ressemblent dans la même
 * liste, et `Intl` ne sait rien faire du second.
 *
 * Le repli sur « fr » est celui de la V1 : `jourLong` et `euros` retombent de toute
 * façon sur la langue du navigateur devant une étiquette qu'ils ne savent pas lire.
 */
export function useLocale(): string {
  const { scope, languages } = useSettings();
  return languages.find((l) => l.id === scope.languageId)?.short ?? "fr";
}

/**
 * ═══ LE MOT DU PRODUIT, DANS LA LANGUE LUE ═══
 *
 * Le `t()` de la V1 (`fo-meeting/src/hooks/useTranslations.jsx`), sur le même magasin et
 * avec la même cascade : langue lue, langue par défaut de l'événement, français, puis la
 * valeur brute. C'est `traduit`, qui la porte pour les libellés de formulaire depuis le
 * début — un second repli écrit ici aurait pu en diverger.
 *
 * ⚠️ LA VALEUR BRUTE RESTE AU POINT D'APPEL, et ce n'est pas un détail de style :
 * `t("CONTINUE", "Continuer")` se lit sans aller chercher ailleurs ce que le bouton dit,
 * et une clé que personne n'a encore remplie n'affiche pas `[CONTINUE]` mais le français.
 * Rien ne change tant que le dictionnaire est muet ; tout se traduit dès qu'il parle.
 *
 * ⚠️ ET C'EST LE MÊME DICTIONNAIRE QUE CELUI DES DÉFAUTS DE RÉGLAGE — voir `useText`,
 * juste en dessous, qui l'interroge sous la clé du réglage. Un seul magasin, une seule
 * cascade : le back office traduit le produit à un seul endroit.
 */
export function useT(): Say {
  const { translations, scope, languages } = useSettings();
  const reference =
    languages.find((l) => l.short === REFERENCE_KEY)?.id ??
    scope.defaultLanguageId;

  return (key, raw, values) => {
    const say = translated(
      translations,
      {
        current: scope.languageId,
        eventDefault: scope.defaultLanguageId,
        reference,
      },
      key,
      raw,
    );
    /**
     * ⚠️ LES TROUS SE NUMÉROTENT, ILS NE SE CONCATÈNENT PAS. « Étape 2 sur 5 » n'a pas
     * le même ordre de mots partout, et découper la phrase en morceaux à recoller aurait
     * figé l'ordre français. C'est la convention `$1`, `$2` de la V1
     * (`useTranslations.jsx`), sur le même dictionnaire : une valeur écrite pour elle
     * fonctionne ici sans retouche.
     */
    return fill(say, values);
  };
}

/**
 * Typed text read with a fallback — by far the most common case.
 *
 * ⚠️ ET LE REPLI PASSE PAR LE DICTIONNAIRE AVANT D'ÊTRE SERVI, sous la clé du réglage
 * lui-même. Sans ça, un défaut du catalogue était UNE chaîne française pour toutes les
 * langues : un événement anglais dont l'organisateur n'avait touché à aucun texte
 * s'affichait intégralement en français, et « Tout traduire » n'y pouvait rien — il ne
 * traduit que ce qui a été ÉCRIT, or justement personne n'avait rien écrit.
 *
 * ⚠️ LES DEUX LECTURES SONT INCONDITIONNELLES : un `return` avant le second appel de
 * hook aurait rendu ce hook-ci conditionnel, ce que React interdit.
 */
export function useText(key: string, fallback = ""): string {
  const raw = useSetting(key);
  const say = useT();
  return typeof raw === "string" && raw.length > 0 ? raw : say(key, fallback);
}

/** Typed boolean read. */
export function useFlag(key: string, fallback = false): boolean {
  const raw = useSetting(key);
  return typeof raw === "boolean" ? raw : fallback;
}

/**
 * A switch that takes its default FROM THE CATALOG — see `onByDefault`.
 *
 * Used wherever "absent means on" is the right reading. Passing `true` by hand at
 * each call site works until one of them forgets, and then the page and the panel
 * disagree about a switch nobody ever touched.
 */
export function useFlagOr(key: string): boolean {
  return useFlag(key, defaultOnOf(key));
}
